import { ICoordinate, ISO8601DateTimeString } from '@autopylot-internal/tiles-client';
import moment from 'moment';
import { Map as ILeafletMap} from 'leaflet';
import { LatLngExpression, MarkerOptions, Marker, CircleMarkerOptions, Circle } from 'leaflet';
import { formatLocalDay, getTimezone } from '../../support/helpers/format';
import { UserFacingError } from '../../support/UserFacingError';
import { State } from '@meraki-internal/state';

// we can't easily export the entire leaflet API as a type, so add this as needed
interface ILeafletGlobal {
    marker(latlng: LatLngExpression, options?: MarkerOptions): Marker;
    circle(latlng: LatLngExpression, options?: CircleMarkerOptions): Circle;
}

// this isn't a complete interface, just what we've used so far
interface IWindyApi {
    map: ILeafletMap;
    store: {
        set: (key: 'timestamp' | 'overlay' | 'level' | 'particlesAnim', value: any) => boolean,
        get: (key: 'overlay' | 'level') => any
    }
}

interface IResetParams {
    center: ICoordinate;
    radiusMeters: number;
    startTime: ISO8601DateTimeString;
    zoom?: number;
    hideMarkers?: boolean;
}

export class WindyMapViewModelConfig {
    DISABLE_MAP?: boolean;
}

export type IWindyLayer = 'snowcover' | 'wind' | 'temp' | 'pressure' | 'clouds' | 'lclouds' | 'mclouds' | 'hclouds' | 'rh' | 'gust' | 'cbase' | 'cape' | 'dewpoint' | 'rain' | 'visibility' | 'deg0' | 'cloudtop' | 'thunder' | 'snowAccu' | 'rainAccu' | 'ptype' | 'sst' | 'gustAccu' | 'ccl';

export type IWindyWindAltitude = 'surface' | '100m';

export class WindyMapViewModel extends State {
    static inject = () => [WindyMapViewModelConfig];

    constructor(private config: WindyMapViewModelConfig){
        super({});

        this.isReady = new Promise(resolve => {
            this.resolveIsReady = resolve;
        });
    }

    isDisabled = () => {
        return this.config.DISABLE_MAP === true;
    };

    isReady: Promise<void>;
    private resolveIsReady!: ()=>void;

    // these do not get set until <BindWindyMapViewModel/> calls setLeaflet(),
    // so check isReady has resolved before attempting to access them
    private leaflet?: ILeafletGlobal;
    private windy?: IWindyApi;
    private mapEl?: Node;
    private permanentMapContainerEl?: Node;

    // these are set by reset and cleared when unmounted from a page
    private missionMarker: Marker | undefined;
    private missionRadiusMarker: Circle | undefined;

    getLayer = (): IWindyLayer => {
        return this.windy!.store.get('overlay');
    };

    setLayer = (layer: IWindyLayer) => {
        this.windy!.store.set('overlay', layer);
        this.windy!.store.set('particlesAnim', layer === 'wind' ? 'on' : 'off');
        this.setState({});
    };

    getWindAltitude = (): IWindyWindAltitude => {
        return this.windy!.store.get('level');
    };

    setWindAltitude = (level: IWindyWindAltitude) => {
        this.windy!.store.set('level', level);
        this.setState({});
    };

    checkForecastAvailable = ({ dateTime, location }: { dateTime: string, location: ICoordinate }) => {
        const timezone = getTimezone(location);
        const offsetDays = moment.tz(dateTime, timezone).startOf('d').diff(moment.tz(timezone).startOf('d'), 'd');

        if (offsetDays < 0 || offsetDays > 9) {

            let displayMessage: string;
            if (offsetDays < 0) {
                displayMessage = 'Not available for missions in the past';
            } else {
                const availableOn = moment.tz(dateTime, timezone).subtract(9, 'days').startOf('day');
                displayMessage = `Forecast will be available ${formatLocalDay({ date: availableOn.toISOString(), location })}`;
            }

            throw new UserFacingError({
                status: 400,
                errorCode: 'forecast.unavailable',
                message: 'Forecast unavailable',
                displayMessage
            });
        }
    };

    reset = ({ center, radiusMeters, startTime, zoom = 10, hideMarkers = false }: IResetParams) => {
        this.checkForecastAvailable({ dateTime: startTime, location: center });

        this.isReady.then(() => {

            // now that we know windy is ready, we can access the private properties above using !. syntax

            this.windy!.map.panTo([center.lat, center.lng]);

            if (hideMarkers) {
                this.removeMarkers();
            } else {
                this.missionMarker = this.leaflet!.marker([center.lat, center.lng]).addTo(this.windy!.map);

                // add a circle for the radius... it mostly is imperctible at the zoom that is relevant for weather, but it adds some depth to
                // marker that looks nice...
                this.missionRadiusMarker = this.leaflet!.circle([center.lat, center.lng], { color: 'white', weight: 1, fillColor: '#23ae09', fillOpacity: 0.3, radius: radiusMeters }).addTo(this.windy!.map);
            }

            this.windy!.map.setZoom(zoom);

            this.windy!.store.set('timestamp', moment(startTime).valueOf());

            this.windy!.store.set('overlay', 'wind');
            this.windy!.store.set('level', 'surface');
        });
    };

    setLeaflet = ({ leafletGlobal, el, windy }: { leafletGlobal: ILeafletGlobal, el: HTMLElement, windy: IWindyApi }) => {
        this.leaflet = leafletGlobal;
        this.windy = windy;
        this.mapEl = el;
        this.permanentMapContainerEl = el.parentNode!;

        new ResizeObserver(() => {
            this.windy!.map.invalidateSize(true);
        }).observe(this.mapEl as Element);

        this.resolveIsReady();
    };

    setZoom = (zoom: number) => {
        this.windy!.map.setZoom(zoom);
        this.setState({ });
    };

    showMapInDomContainer = async (temporaryParentContainer: HTMLElement) => {
        this.isReady.then(() => {

            // now that we know windy is ready, we can access the private properties above using !. syntax

            temporaryParentContainer.appendChild(this.mapEl!);
        });
    };

    private removeMarkers = () => {
        if (this.missionMarker){
            this.missionMarker.remove();
        }
        if (this.missionRadiusMarker){
            this.missionRadiusMarker.remove();
        }
    };

    removeMapFromDomContainer = () => {
        this.isReady.then(() => {

            // now that we know windy is ready, we can access the private properties above using !. syntax

            this.permanentMapContainerEl!.appendChild(this.mapEl!);

            this.removeMarkers();
        });
    };
}
